import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Theme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/styles';
import React, { FunctionComponent, useState } from 'react';
import { useAuthUser } from '../../../../../contexts/AuthProvider/hooks';
import {
  hasOneOfRoles,
  Role,
} from '../../../../../contexts/AuthProvider/types';
import ButtonProgress from '../../../../../eliot-components/src/ButtonProgress/ButtonProgress';
import { useConfig } from '../../../../../eliot-components/src/ConfigProvider/hooks';
import { useCreateOrgMutation } from '../../../../../store/api-slice';
import { useSnackbar } from 'notistack';
import { parseErrorMessage } from '../../../../../eliot-components/src/utils/helpers';

const useStyles = makeStyles((theme: Theme) => ({
  fab: {
    position: 'fixed',
    right: theme.spacing(4),
    bottom: theme.spacing(4),
    [theme.breakpoints.down('xs')]: {
      right: theme.spacing(2),
      bottom: theme.spacing(2),
    },
  },
  dialogContent: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  dialog: {
    '& .MuiDialog-paperFullWidth': {
      minWidth: 0,
    },
    '& .MuiFormGroup-root': {
      marginBottom: 5,
    },
  },
  orgName: {
    '& .MuiFormHelperText-root': {
      color: theme.palette.primary.main,
    },
  },
}));

interface Props {
  onCreated?(name: string): void;
  onClose(): void;
  isOpen: boolean;
}

const CreateOrgDialog: FunctionComponent<Props> = ({
  onCreated,
  onClose,
  isOpen,
}) => {
  const classes = useStyles();
  const { authUser } = useAuthUser();
  const { apiBaseURL } = useConfig();
  const [createOrg, { isLoading }] = useCreateOrgMutation();
  const [name, setName] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  if (!hasOneOfRoles(authUser, [Role.OrganizationManager])) {
    return null;
  }

  const handleClose = () => {
    setName('');
    onClose();
  };

  return (
    <>
      <Dialog
        className={classes.dialog}
        fullWidth
        maxWidth="sm"
        aria-labelledby="dialog-title"
        open={isOpen}
        onClose={() => {
          handleClose();
        }}
      >
        <DialogTitle id="dialog-title">Create Org</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <TextField
            margin="dense"
            variant="outlined"
            id="name"
            label="Name"
            inputProps={{ maxLength: 60 }}
            className={classes.orgName}
            autoFocus
            fullWidth
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
            helperText={
              name && name.length === 60
                ? 'Maximum character length reached'
                : null
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              handleClose();
            }}
          >
            Cancel
          </Button>
          <ButtonProgress
            variant="contained"
            color="primary"
            isLoading={isLoading}
            disabled={name.length === 0}
            onClick={() => {
              createOrg({ name, apiBaseURL }).then(
                (resp: any) => {
                  if (!resp.error) {
                    if (onCreated) {
                      onCreated(name);
                      return;
                    }
                    handleClose();
                  }

                  enqueueSnackbar(parseErrorMessage(resp.error), {
                    variant: 'error',
                  });
                },
                () => handleClose()
              );
            }}
          >
            Submit
          </ButtonProgress>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateOrgDialog;
